import Delta from "quill-delta";
import { canv } from "@/canv/global";
import { getBoolProperty, getNumberProperty, getStringProperty } from "@/canv/utils";
import { getViewport } from "@/canvasController/helpers";
import { ElementBase } from "@/elementBase";
import { hasFontVariant } from "@/fonts/catalog";
import {
    ensureFontLoaded,
    ensureFontVariantLoaded,
    getFontRequirementsForTextData,
    isFontRequirementsLoaded,
} from "@/fonts/fontRegistry";
import { selectionManager } from "@/tools/cursorTool";
import { getFontSize } from "@/utils";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import {
    DEFAULT_FONT_SIZE,
    MIN_TEXT_WIDTH,
    RESIZE_FONT_THRESHOLD_PX,
    RESIZE_WIDTH_THRESHOLD_PX,
    STICKER_MIN_FONT_SIZE,
    TEXT_RATIO_FOR_PLACEHOLDER,
} from "./constantsV2";
import type { TextLinkRunMetric } from "./linkPopup";
import type {
    DeferredResize,
    DiagonalResizeState,
    PendingResizePayload,
    ResizeMode,
    SelectionSummary,
    StyleSummary,
    TextAlign,
    TextLine,
    TextV2Data,
    TextVerticalAlign,
    VerticalResizeState,
    WidgetStateMap,
} from "./typesV2";
import {
    buildDeltaWithTextAlign,
    buildLinkRuns,
    calculateLineHeight,
    calculateLineWidth,
    calculateLineWidths,
    cloneLines,
    createDefaultTextData,
    deltaToLines,
    drawLine,
    drawLinePlaceholder,
    editingResizeDiagonal,
    editingResizeVertical,
    findBestStickerLayout,
    getLineStartX,
    getMaxLineWidth,
    getSegmentFont,
    getStickerMinFontSize,
    getTextStartX,
    measureTextWidth,
    normalizeColors,
    reflowTextLines,
    resizeDiagonal,
    resizeVertical,
    sanitizeDeltaLinkColor,
    summarizeDelta,
    updateListNumber,
    wrapLinesByMaxSymbols,
} from "./utilsV2";

export class TextWidgetV2 extends ElementBase {
    constructor(startX: number = 0, startY: number = 0, data?: TextV2Data) {
        super();
        this.data = data || createDefaultTextData();
        this.fontSize = this.data.fontSize;
        this.startX = startX;
        this.startY = startY;
        this.styleSummary = summarizeDelta(this.data.delta);

        this.rawOriginalLines = deltaToLines(this.data.delta);
        this.rawOriginalLines = cloneLines(this.rawOriginalLines);

        const metrics = calculateLineHeight(this.data);

        if (!data) {
            const initWidth = Math.max(MIN_TEXT_WIDTH, metrics.symbolWidth * 10);
            this.buildLayout(initWidth, true);
        } else {
            this.buildLayout();
        }
    }

    private setFontSizeValue(fontSize: number) {
        this.fontSize = fontSize;
        this.data.fontSize = fontSize;
    }

    private pickTargetWidth(naturalWidth: number, preferWidth?: number) {
        if (this.resizeMode === "shape" && this.hardWidth > 0) {
            return Math.max(MIN_TEXT_WIDTH, this.hardWidth);
        }

        const minW = MIN_TEXT_WIDTH;

        const preferred = preferWidth ? Math.max(minW, preferWidth) : 0;

        return Math.max(naturalWidth, preferred, minW);
    }

    private buildLayout(preferWidth?: number, isEmptyInit?: boolean) {
        const metrics = calculateLineHeight(this.data);
        this.lineHeight = metrics.lineHeight;
        this.verticalPadding = metrics.verticalPadding;
        this.horizontalPadding = metrics.horizontalPadding;

        this.ensureLinePresence();

        const normalized = wrapLinesByMaxSymbols(this.rawOriginalLines);

        const naturalLineWidths = calculateLineWidths(
            normalized,
            this.data,
            this.horizontalPadding,
        );
        const naturalWidth = getMaxLineWidth(naturalLineWidths);

        const targetWidth = this.pickTargetWidth(naturalWidth, preferWidth);

        const reflow = reflowTextLines(
            normalized,
            targetWidth,
            this.data,
            this.horizontalPadding,
            this.resizeMode !== "sticker" || this.fontSize <= STICKER_MIN_FONT_SIZE,
        );
        this.lines = reflow.lines;
        this.lineWidths = reflow.lineWidths;

        if (isEmptyInit) {
            this.lineWidths[0] = targetWidth - 2 * this.horizontalPadding;
        }

        this.applyLineHeights(this.lines);
        this.updateDims();
        this.rebuildLinkRuns();
    }

    calculateLineHeight() {
        const { lineHeight, horizontalPadding, verticalPadding } = calculateLineHeight(this.data);
        this.lineHeight = lineHeight;
        this.horizontalPadding = horizontalPadding;
        this.verticalPadding = verticalPadding;
    }

    updateDims(): void {
        let maxWidth: number;
        if ((this.resizeMode === "shape" || this.resizeMode === "sticker") && this.hardWidth > 0) {
            maxWidth = this.hardWidth;
        } else {
            maxWidth = getMaxLineWidth(this.lineWidths);
        }
        const totalHeight = this.lines.length * this.lineHeight;
        this.width = maxWidth;
        this.height = totalHeight;
        this.applyLineHeights(this.lines);
        this.updateDrawStartX();

        this.updateBase(this.startX, this.startY - this.lineHeight, maxWidth, totalHeight);
    }

    draw(): void {
        if (this.isEditing || canv.textEditingController.isEditingWidget(this)) {
            return;
        }

        canv.ctx.save();
        const viewport = getViewport();
        const drawPlaceholder =
            this.lineHeight / viewport.viewportHeight <= TEXT_RATIO_FOR_PLACEHOLDER;
        const shouldDrawPlaceholder = drawPlaceholder && !canv.isHighPdfExporting;

        if (shouldDrawPlaceholder) {
            canv.ctx.fillStyle = "#9c9c9c";
        }

        let currentLineY = this.startY;
        let listNumber = 1;

        for (let index = 0; index < this.lines.length; index++) {
            const line = this.lines[index];
            if (line.segments.length === 0) {
                listNumber = 1;
                currentLineY += this.lineHeight;
                continue;
            }

            const lineWidth = this.getLineWidthForDraw(line, index);
            const lineForRender =
                line.listType === "ordered" && line.listNumber == null
                    ? { ...line, listNumber }
                    : line;
            if (shouldDrawPlaceholder) {
                drawLinePlaceholder(
                    lineForRender,
                    currentLineY,
                    this.data,
                    this.drawStartX,
                    this.horizontalPadding,
                    this.verticalPadding,
                    this.lineHeight,
                    lineWidth,
                );
            } else {
                drawLine(
                    lineForRender,
                    currentLineY,
                    this.data,
                    this.drawStartX,
                    this.horizontalPadding,
                    this.verticalPadding,
                    this.lineHeight,
                    lineWidth,
                );
            }
            listNumber = updateListNumber(lineForRender, listNumber);
            currentLineY += this.lineHeight;
        }

        canv.ctx.restore();
    }

    translate(x: number, y: number): void {
        this.startX += x;
        this.startY += y;

        //Добавлено условие для paste сценария, т.к. после вставки текста происходит translate, при проблемах с translate стоит попытаться обработать иначе
        if (this.isEditing) {
            this.updateDrawStartX();
            this.updateBase(this.startX, this.startY - this.lineHeight, this.width, this.height);
            return;
        }

        this.updateDims();
    }

    resize(x: number, y: number, catchPointer: string): void {
        const isCornerResize = ["bottomRight", "bottomLeft", "upRight", "upLeft"].includes(
            catchPointer,
        );
        const isSideResize = ["sideRight", "sideLeft"].includes(catchPointer);
        const isVerticalResize = ["sideTop", "sideBottom"].includes(catchPointer);

        if (!isCornerResize && !isSideResize && !isVerticalResize) return;

        if (!this.resizeStartCatchPointer) {
            this.resizeStartCatchPointer = catchPointer;
            if (this.resizeMode !== "shape") {
                this.resizeMode = "shape";
                this.hardWidth =
                    this.baseWidth > 0 ? this.baseWidth : getMaxLineWidth(this.lineWidths);
            }
            this.resizeDiagonalState = isCornerResize
                ? {
                      initialFontSize: this.data.fontSize,
                      initialWidth: this.hardWidth,
                      accumulatedX: 0,
                      initialLines: cloneLines(this.lines),
                  }
                : null;
            this.resizeVerticalState = isVerticalResize
                ? {
                      initialFontSize: this.data.fontSize,
                      initialHeight: this.lines.length * this.lineHeight,
                      initialWidth: this.hardWidth,
                      accumulatedY: 0,
                      initialLines: cloneLines(this.lines),
                  }
                : null;
        }

        if (this.isEditing) {
            if (!this.deferredResize) {
                this.deferredResize = { catchPointer: this.resizeStartCatchPointer, x: 0, y: 0 };
            }

            this.deferredResize.x += x;
            this.deferredResize.y += y;

            if (isSideResize) {
                this.hardWidth = Math.max(1, this.hardWidth + x);
                if (this.hardWidth < MIN_TEXT_WIDTH) this.hardWidth = MIN_TEXT_WIDTH;
                this.updateDims();
                canv.textEditingController.updateOverlayTransform();
                return;
            }

            if (isCornerResize) {
                if (!this.resizeDiagonalState) {
                    return;
                }

                const { nextFontSize, nextWidth } = editingResizeDiagonal({
                    resizeState: this.resizeDiagonalState,
                    deferredResize: this.deferredResize,
                });

                if (nextFontSize >= 1) {
                    this.hardWidth = nextWidth;
                    this.setFontSizeValue(nextFontSize);

                    const metrics = calculateLineHeight(this.data);
                    this.lineHeight = metrics.lineHeight;
                    this.verticalPadding = metrics.verticalPadding;
                    this.horizontalPadding = metrics.horizontalPadding;

                    this.updateDims();
                    canv.textEditingController.updateOverlayTransform(nextWidth);
                }
                return;
            }

            // vertical resize
            if (isVerticalResize) {
                if (!this.resizeVerticalState) {
                    return;
                }

                const { nextFontSize, nextWidth } = editingResizeVertical({
                    resizeState: this.resizeVerticalState,
                    deferredResize: this.deferredResize,
                });

                this.setFontSizeValue(nextFontSize);

                const metrics = calculateLineHeight(this.data);
                this.hardWidth = nextWidth;
                this.lineHeight = metrics.lineHeight;
                this.verticalPadding = metrics.verticalPadding;
                this.horizontalPadding = metrics.horizontalPadding;

                this.updateDims();
                canv.textEditingController.updateOverlayTransform(nextWidth);
                return;
            }

            return;
        }

        if (isCornerResize) {
            const diagonalResizeState =
                this.pendingResizePayload?.kind === "corner"
                    ? this.pendingResizePayload.diagonalResizeState
                    : this.resizeDiagonalState;
            const lines =
                this.pendingResizePayload?.kind === "corner"
                    ? this.pendingResizePayload.lines
                    : this.lines;
            const result = resizeDiagonal(x, {
                diagonalResizeState,
                data: this.data,
                lines,
            });

            if (!result) {
                return;
            }

            this.scheduleResizeApply({
                kind: "corner",
                hardWidth: result.hardWidth,
                fontSize: result.fontSize,
                lineHeight: result.lineHeight,
                verticalPadding: result.verticalPadding,
                horizontalPadding: result.horizontalPadding,
                lines: result.lines,
                lineWidths: result.lineWidths,
                diagonalResizeState: result.diagonalResizeState,
            });
            return;
        }

        if (isSideResize) {
            if (x === 0) {
                return;
            }

            const baseHardWidth =
                this.pendingResizePayload?.kind === "side"
                    ? this.pendingResizePayload.hardWidth
                    : this.hardWidth;
            const nextHardWidth = Math.max(MIN_TEXT_WIDTH, baseHardWidth + x);
            this.scheduleResizeApply({
                kind: "side",
                hardWidth: nextHardWidth,
            });
            return;
        }

        const resizeState =
            this.pendingResizePayload?.kind === "vertical"
                ? this.pendingResizePayload.resizeState
                : this.resizeVerticalState;
        const lines =
            this.pendingResizePayload?.kind === "vertical"
                ? this.pendingResizePayload.lines
                : this.lines;
        const result = resizeVertical(y, {
            resizeState,
            data: this.data,
            lines,
        });
        if (!result) return;

        this.scheduleResizeApply({
            kind: "vertical",
            hardWidth: result.hardWidth,
            fontSize: result.fontSize,
            lineHeight: result.lineHeight,
            verticalPadding: result.verticalPadding,
            horizontalPadding: result.horizontalPadding,
            lines: result.lines,
            lineWidths: result.lineWidths,
            resizeState: result.resizeState,
        });
    }

    resizingStart() {
        this.resizeStartCatchPointer = null;
        this.resizeDiagonalState = null;
        this.resizeVerticalState = null;

        this.isResizeSessionActive = true;
        this.pendingResizePayload = null;
        this.appliedResizeSignature = null;
        this.stickerResizeAnchor =
            this.resizeMode === "sticker" && this.lines.length > 0
                ? {
                      hardWidth: this.hardWidth,
                      hardHeight: this.hardHeight,
                      fontSize: this.fontSize,
                      lines: cloneLines(this.lines),
                  }
                : null;

        if (this.resizeRafId !== null) {
            cancelAnimationFrame(this.resizeRafId);
            this.resizeRafId = null;
        }
    }

    resizingStop = (): void => {
        if (this.resizeMode === "sticker") {
            this.stickerResizeAnchor = null;
            this.pendingResizePayload = null;
            this.appliedResizeSignature = null;
            this.isResizeSessionActive = false;
            this.fitSticker(true);
            return;
        }

        if (!this.resizeStartCatchPointer || this.isEditing) {
            return;
        }

        this.flushPendingResize();

        const isVerticalResize = ["sideTop", "sideBottom"].includes(this.resizeStartCatchPointer);
        this.resizeStartCatchPointer = null;
        if (isVerticalResize && this.resizeVerticalState) {
            this.lineWidths = calculateLineWidths(this.lines, this.data, this.horizontalPadding);
            const scale = this.data.fontSize / this.resizeVerticalState.initialFontSize;
            const potentialHardWidth = this.resizeVerticalState.initialWidth * scale;

            this.hardWidth = Math.max(potentialHardWidth, MIN_TEXT_WIDTH);
            this.updateDims();
            this.pendingResizePayload = null;
            this.appliedResizeSignature = null;
            this.isResizeSessionActive = false;
            return;
        }
        if (this.hardWidth < MIN_TEXT_WIDTH) {
            this.hardWidth = MIN_TEXT_WIDTH;
        }
        const reflow = reflowTextLines(
            this.rawOriginalLines,
            this.hardWidth,
            this.data,
            this.horizontalPadding,
        );
        this.lines = reflow.lines;
        this.lineWidths = reflow.lineWidths;
        this.applyLineHeights(this.lines);
        this.updateDims();
        this.pendingResizePayload = null;
        this.appliedResizeSignature = null;
        this.isResizeSessionActive = false;
    };

    commitDeferredResize(): void {
        if (!this.deferredResize) {
            return;
        }

        const { catchPointer } = this.deferredResize;
        this.deferredResize = null;

        const isCornerResize = ["bottomRight", "bottomLeft", "upRight", "upLeft"].includes(
            catchPointer,
        );
        const isSideResize = ["sideRight", "sideLeft"].includes(catchPointer);
        const isVerticalResize = ["sideTop", "sideBottom"].includes(catchPointer);

        if (isSideResize) {
            const nextHardWidth = Math.max(MIN_TEXT_WIDTH, this.hardWidth);
            const reflow = reflowTextLines(
                this.rawOriginalLines,
                nextHardWidth,
                this.data,
                this.horizontalPadding,
            );
            this.lines = reflow.lines;
            this.lineWidths = reflow.lineWidths;
            this.hardWidth = nextHardWidth;
        } else if (isCornerResize || isVerticalResize) {
            const reflow = reflowTextLines(
                this.rawOriginalLines,
                Math.max(MIN_TEXT_WIDTH, this.hardWidth),
                this.data,
                this.horizontalPadding,
            );
            this.lines = reflow.lines;
            this.lineWidths = reflow.lineWidths;
        }

        this.updateDims();
        this.rebuildLinkRuns();
    }

    private scheduleResizeApply(payload: PendingResizePayload) {
        this.pendingResizePayload = payload;

        if (this.resizeRafId !== null) {
            return;
        }

        this.resizeRafId = requestAnimationFrame(() => {
            this.resizeRafId = null;
            this.applyPendingResizeFrame();
        });
    }

    syncPendingResizeGeometryForTransform(): {
        startX: number;
        startY: number;
        width: number;
        height: number;
    } | null {
        if (!this.isResizeSessionActive || !this.pendingResizePayload) {
            return null;
        }

        const { pendingResizePayload } = this;
        if (pendingResizePayload.kind !== "side") {
            this.applyResizePayload(pendingResizePayload);
        } else {
            this.hardWidth = pendingResizePayload.hardWidth;
            this.width = pendingResizePayload.hardWidth;
            this.height = this.lines.length * this.lineHeight;
            this.updateDrawStartX();
        }

        this.updateBase(this.startX, this.startY - this.lineHeight, this.width, this.height, false);

        return {
            startX: this.startX,
            startY: this.startY - this.lineHeight,
            width: this.width,
            height: this.height,
        };
    }

    private applyPendingResizeFrame(force = false) {
        if (!this.isResizeSessionActive) {
            this.pendingResizePayload = null;
            return;
        }

        const payload = this.pendingResizePayload;
        if (!payload) {
            return;
        }

        const signature = this.getResizePayloadSignature(payload);
        if (!force && signature === this.appliedResizeSignature) {
            return;
        }

        this.pendingResizePayload = null;
        this.applyResizePayload(payload);
        this.appliedResizeSignature = signature;

        this.updateDims();
        canv.redraw();

        if (this.pendingResizePayload) {
            this.resizeRafId = requestAnimationFrame(() => {
                this.resizeRafId = null;
                this.applyPendingResizeFrame();
            });
        }
    }

    private applyResizePayload(payload: PendingResizePayload): void {
        if (payload.kind === "side") {
            this.hardWidth = payload.hardWidth;

            const reflow = reflowTextLines(
                this.rawOriginalLines,
                this.hardWidth,
                this.data,
                this.horizontalPadding,
                this.resizeMode !== "sticker" || this.fontSize <= STICKER_MIN_FONT_SIZE,
            );

            this.lines = reflow.lines;
            this.lineWidths = reflow.lineWidths;
            this.applyLineHeights(this.lines);
            return;
        }

        if (payload.kind === "corner") {
            this.resizeDiagonalState = payload.diagonalResizeState;
            this.hardWidth = payload.hardWidth;
            this.setFontSizeValue(payload.fontSize);
            this.lineHeight = payload.lineHeight;
            this.verticalPadding = payload.verticalPadding;
            this.horizontalPadding = payload.horizontalPadding;
            this.lines = payload.lines;
            this.lineWidths = payload.lineWidths;
            this.applyLineHeights(this.lines);
            return;
        }

        this.resizeVerticalState = payload.resizeState;
        this.hardWidth = payload.hardWidth;
        this.setFontSizeValue(payload.fontSize);
        this.lineHeight = payload.lineHeight;
        this.verticalPadding = payload.verticalPadding;
        this.horizontalPadding = payload.horizontalPadding;
        this.lines = payload.lines;
        this.lineWidths = payload.lineWidths;
        this.applyLineHeights(this.lines);
    }

    private getResizePayloadSignature(payload: PendingResizePayload) {
        const getWidthBucket = (value: number) =>
            Math.round(value / RESIZE_WIDTH_THRESHOLD_PX) * RESIZE_WIDTH_THRESHOLD_PX;
        const getFontBucket = (value: number) =>
            Math.round(value / RESIZE_FONT_THRESHOLD_PX) * RESIZE_FONT_THRESHOLD_PX;

        switch (payload.kind) {
            case "side":
                return `side:${getWidthBucket(payload.hardWidth)}`;
            case "corner":
                return `corner:${getWidthBucket(payload.hardWidth)}:${getFontBucket(payload.fontSize)}`;
            case "vertical":
                return `vertical:${getWidthBucket(payload.hardWidth)}:${getFontBucket(payload.fontSize)}`;
        }
    }

    private flushPendingResize(): void {
        if (this.resizeRafId !== null) {
            cancelAnimationFrame(this.resizeRafId);
            this.resizeRafId = null;
        }

        if (this.pendingResizePayload) {
            this.applyPendingResizeFrame(true);
        }
    }

    commitDefferedChanges() {
        let shouldRedraw = false;

        if (this.deferredResize) {
            this.commitDeferredResize();
            shouldRedraw = true;
        }

        if (this.parent?.angle) {
            shouldRedraw = true;
        }

        if (shouldRedraw) {
            canv.redraw();
        }
    }

    getWidgetOptions() {
        const widgetOptions: widgetOption[] = [];

        widgetOptions.push(new widgetOption("textFont", this.data.fontFamily, this.UID));
        widgetOptions.push(new widgetOption("textSize", this.data.fontSize, this.UID));
        widgetOptions.push(new widgetOption("textColor", this.styleSummary.firstColor, this.UID));

        if (this.styleSummary.firstColor !== this.styleSummary.secondColor) {
            widgetOptions.push(
                new widgetOption("textColor", this.styleSummary.secondColor, this.UID),
            );
        }

        widgetOptions.push(new widgetOption("textBold", this.styleSummary.isBold, this.UID));
        widgetOptions.push(new widgetOption("textStrike", this.styleSummary.isStriked, this.UID));
        widgetOptions.push(new widgetOption("textItalic", this.styleSummary.isItalic, this.UID));
        widgetOptions.push(
            new widgetOption("textUnderline", this.styleSummary.isUnderline, this.UID),
        );
        widgetOptions.push(
            new widgetOption("textBulleted", this.styleSummary.hasBulletList, this.UID),
        );
        widgetOptions.push(new widgetOption("textAlign", this.data.textAlign, this.UID));

        widgetOptions.push(new widgetOption("link", this.styleSummary.link, this.UID));

        return widgetOptions;
    }

    getHandlerForOption(name: string) {
        switch (name) {
            case "textColor":
                return this.menuTextColor;

            case "textBold":
                return this.menuTextBold;

            case "textStrike":
                return this.menuTextStrike;

            case "textItalic":
                return this.menuTextItalic;

            case "textUnderline":
                return this.menuTextUnderline;

            case "textAlign":
                return this.menuTextAlign;

            case "textVerticalAlign":
                return this.menuTextVerticalAlign;

            case "textBulleted":
                return this.menuTextBulleted;

            case "textSize":
                return this.menuTextSize;

            case "textFont":
                return this.menuTextFont;

            case "link":
                return this.menuTextLink;
        }
    }

    menuTextColor(value: string, forceAll = false) {
        if (this.isEditing) {
            const isForcedColorMode =
                forceAll && (this.resizeMode === "shape" || this.resizeMode === "sticker");

            if (isForcedColorMode) {
                canv.textEditingController.setForcedTextColor(value);
            } else if (forceAll) {
                canv.textEditingController.applyTextFormattingToAll("color", value);
            } else {
                canv.textEditingController.applyTextFormatting("color", value);
            }
        } else {
            if (this.symbolsCount === 0) {
                this.styleSummary.firstColor = value;
                this.styleSummary.secondColor = value;
                return;
            }

            const newDelta = canv.textEditingController.patchDelta("color", value, this.data.delta);
            this.setNewDelta(newDelta);
        }
    }

    async menuTextBold(value: boolean) {
        const booleanValue = !!value;
        if (booleanValue) {
            const isVariantLoaded = await ensureFontVariantLoaded(this.data.fontFamily, "bold");
            if (!isVariantLoaded) {
                return false;
            }

            // Догружаем bold-italic только когда italic реально включен в форматировании.
            if (
                this.styleSummary.isItalic === true &&
                hasFontVariant(this.data.fontFamily, "bold-italic")
            ) {
                await ensureFontVariantLoaded(this.data.fontFamily, "bold-italic");
            }
        }

        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("bold", booleanValue);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "bold",
                booleanValue,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    menuTextStrike(value: boolean) {
        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("strike", value);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "strike",
                value,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    async menuTextItalic(value: boolean) {
        if (value) {
            const isVariantLoaded = await ensureFontVariantLoaded(this.data.fontFamily, "italic");
            if (!isVariantLoaded) {
                return false;
            }

            // Догружаем bold-italic только когда bold реально включен в форматировании.
            if (
                this.styleSummary.isBold === true &&
                hasFontVariant(this.data.fontFamily, "bold-italic")
            ) {
                await ensureFontVariantLoaded(this.data.fontFamily, "bold-italic");
            }
        }

        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("italic", value);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "italic",
                value,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    menuTextUnderline(value: boolean) {
        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("underline", value);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "underline",
                value,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    menuTextAlign(value: TextAlign) {
        if (value === this.data.textAlign) {
            return;
        }

        this.data.textAlign = value;
        const quillAlignValue = value === "left" ? false : value;

        if (this.isEditing) {
            canv.textEditingController.applyTextFormattingToAll("align", quillAlignValue);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "align",
                quillAlignValue,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    menuTextVerticalAlign(value: TextVerticalAlign) {
        if (value === this.data.textVerticalAlign) {
            return;
        }
        this.data.textVerticalAlign = value;
    }

    menuTextBulleted(value: boolean) {
        const normalizedValue = value ? "bullet" : value;

        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("list", normalizedValue);
        } else {
            const newDelta = canv.textEditingController.patchDelta(
                "list",
                normalizedValue,
                this.data.delta,
            );
            this.setNewDelta(newDelta);
        }
    }

    menuTextLink(value: string) {
        canv.widgetMenu.hide();
        canv.textEditingController.showSelectionRects();
        canv.appInterface.linkInputController.show(value, this, null);
    }

    menuLinkSet(link: string) {
        const value = link || false;
        canv.textEditingController.hideSelectionRects();

        if (this.isEditing) {
            canv.textEditingController.applyTextFormatting("link", value);
        } else {
            const newDelta = canv.textEditingController.patchDelta("link", value, this.data.delta);
            this.setNewDelta(newDelta);
        }

        canv.widgetMenu.reopen();
    }

    menuTextSize(value: number) {
        const oldFontSize = this.fontSize;
        this.setFontSizeValue(getFontSize(value));
        const metrics = calculateLineHeight(this.data);
        this.lineHeight = metrics.lineHeight;
        this.verticalPadding = metrics.verticalPadding;
        this.horizontalPadding = metrics.horizontalPadding;

        if (this.resizeMode === "shape") {
            const compression = value / oldFontSize;

            this.hardWidth = this.hardWidth * compression;
        }

        if (this.isEditing) {
            canv.textEditingController.syncEditorAfterStyleChange();

            const isTextChanged = canv.textEditingController.getIsTextChanged();

            if (!isTextChanged) {
                this.reflowFromRawLines();
            }
        } else {
            this.reflowFromRawLines();
        }

        canv.widgetMenu.TryLocateMenu();
    }

    async menuTextFont(value: string) {
        if (!value || value === this.data.fontFamily) {
            return false;
        }

        const requestId = ++this.menuTextFontRequestId;
        const requirements = getFontRequirementsForTextData(this.rawOriginalLines);

        try {
            await ensureFontLoaded(value, requirements);
        } catch {
            return false;
        }

        if (
            requestId !== this.menuTextFontRequestId ||
            !isFontRequirementsLoaded(value, requirements)
        ) {
            return false;
        }

        this.data.fontFamily = value;
        this.reflowFromRawLines();

        if (this.isEditing) {
            canv.textEditingController.syncEditorAfterStyleChange();
        }

        return true;
    }

    Fit(): void {
        if (this.resizeMode === "sticker") {
            this.fitSticker();
        } else if (this.resizeMode === "shape") {
            this.fitShape();
        }
    }

    fitShape(): void {
        if (this.isEditing) {
            return;
        }

        if (this.hardWidth <= 0) {
            this.hardWidth = getMaxLineWidth(this.lineWidths);
        }
        // убрал wrapLinesByMaxSymbols, заменив на this.rawOriginalLines,
        // так как были баги при повторных расчетах одного и того же текста в таблице
        const normalizedLines = this.rawOriginalLines;
        const reflow = reflowTextLines(
            normalizedLines,
            this.hardWidth,
            this.data,
            this.horizontalPadding,
        );
        this.lines = reflow.lines;
        this.lineWidths = reflow.lineWidths;
        this.applyLineHeights(this.lines);
        this.width = this.hardWidth;
        this.height = this.lines.length * this.lineHeight;
        this.updateDrawStartX();
        this.rebuildLinkRuns();
    }

    fitSticker(force = false): void {
        if ((!force && this.isEditing) || this.hardWidth <= 0 || this.hardHeight <= 0) {
            return;
        }

        // Для пустого текста нужна базовая строка, чтобы fitSticker
        // корректно вычислил стартовые метрики и размер каретки.
        this.ensureLinePresence();

        if (this.isResizeSessionActive && this.stickerResizeAnchor) {
            this.fitStickerWithAnchor();
            return;
        }

        const {
            lineHeight,
            lineWidths,
            lines,
            fontSize,
            verticalPadding,
            horizontalPadding,
            width,
            height,
        } = findBestStickerLayout({
            data: this.data,
            sourceLines: this.rawOriginalLines,
            hardWidth: this.hardWidth,
            hardHeight: this.hardHeight,
            minFontSize: getStickerMinFontSize(this.hardWidth, this.hardHeight),
            maxFontSize: Math.max(
                getStickerMinFontSize(this.hardWidth, this.hardHeight),
                this.hardHeight,
                this.hardWidth,
                this.fontSize,
            ),
        });

        this.setFontSizeValue(fontSize);
        this.lineHeight = lineHeight;
        this.verticalPadding = verticalPadding;
        this.horizontalPadding = horizontalPadding;
        this.lines = lines;
        this.lineWidths = lineWidths;
        this.applyLineHeights(this.lines);
        this.width = width;
        this.height = height;
        this.rebuildLinkRuns();
    }

    private fitStickerWithAnchor() {
        const anchor = this.stickerResizeAnchor;
        if (!anchor || anchor.hardWidth <= 0 || anchor.hardHeight <= 0) {
            return;
        }

        const scaleByWidth = this.hardWidth / anchor.hardWidth;
        const scaleByHeight = this.hardHeight / anchor.hardHeight;
        const scale = Math.max(0, Math.min(scaleByWidth, scaleByHeight));
        const nextFontSize = Math.max(1, anchor.fontSize * scale);

        const nextData: TextV2Data = { ...this.data, fontSize: nextFontSize };
        let metrics = calculateLineHeight(nextData);
        let nextLineWidths = calculateLineWidths(anchor.lines, nextData, metrics.horizontalPadding);
        let nextWidth = getMaxLineWidth(nextLineWidths);
        let nextHeight = anchor.lines.length * metrics.lineHeight;

        if (nextWidth > this.hardWidth || nextHeight > this.hardHeight) {
            const widthFitScale = nextWidth > 0 ? this.hardWidth / nextWidth : 1;
            const heightFitScale = nextHeight > 0 ? this.hardHeight / nextHeight : 1;
            const overflowScale = Math.max(0, Math.min(widthFitScale, heightFitScale));
            const adjustedFontSize = Math.max(1, nextFontSize * overflowScale);
            const adjustedData: TextV2Data = { ...this.data, fontSize: adjustedFontSize };
            metrics = calculateLineHeight(adjustedData);
            nextLineWidths = calculateLineWidths(
                anchor.lines,
                adjustedData,
                metrics.horizontalPadding,
            );
            nextWidth = getMaxLineWidth(nextLineWidths);
            nextHeight = anchor.lines.length * metrics.lineHeight;
            this.setFontSizeValue(adjustedFontSize);
        } else {
            this.setFontSizeValue(nextFontSize);
        }

        this.lineHeight = metrics.lineHeight;
        this.verticalPadding = metrics.verticalPadding;
        this.horizontalPadding = metrics.horizontalPadding;
        this.lines = cloneLines(anchor.lines);
        this.lineWidths = nextLineWidths;
        this.applyLineHeights(this.lines);
        this.width = nextWidth;
        this.height = nextHeight;
        this.rebuildLinkRuns();
    }

    TryAction(type: string): void {
        if (canv.appInterface.linkInputController.isInputVisible) {
            if (type === "touchpadGestureDragging" || type === "touchpadGestureZoom") {
                canv.appInterface.linkInputController.locate();
            }
            return;
        }

        if (type === "mousedown") {
            canv.textEditingController.startEdit(this, true);
            this.isEditing = true;
        }

        if (type === "keyboardDown") {
            if (canv.keyboard.isEscCommand()) {
                if (this.parent !== null) {
                    canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
                }
            }
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "insertText") {
            this.fixState("focus");
            canv.textEditingController.startEdit(this);
            this.isEditing = true;

            if (canv.keyboard.isSymbol && !canv.keyboard.isEnter() && canv.keyboard.command) {
                canv.textEditingController.insertText(canv.keyboard.command, true);
            }

            return {
                type: "interaction",
            };
        }

        if (context === "pastText") {
            this.fixState("focus");
            canv.textEditingController.startEdit(this);
            this.isEditing = true;

            if (canv.keyboard.isPasteCommand()) {
                void canv.textEditingController.pasteFromClipboard(true);
            }

            return {
                type: "interaction",
            };
        }

        if ((context === "focus" && this.isOver()) || force) {
            this.fixState("focus");
            if (!context) {
                canv.textEditingController.startEdit(this);
            } else {
                canv.textEditingController.startEdit(this, true);
            }
            this.isEditing = true;
            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    skipNextStopInteractionSave() {
        this.skipNextInteractionSave = true;
    }

    skipNextHostInteractionSaveCommit() {
        this.skipNextHostInteractionSave = true;
    }

    consumeHostInteractionSaveSkip() {
        const shouldSkip = this.skipNextHostInteractionSave;
        this.skipNextHostInteractionSave = false;

        return shouldSkip;
    }

    setEditingAutosaveHandler(handler: (() => void) | false | null) {
        this.editingAutosaveHandler = handler;
    }

    stopInteraction(shouldSave = true): void {
        const shouldPersist = shouldSave && !this.skipNextInteractionSave;
        this.skipNextInteractionSave = false;

        if (this.symbolsCount === 0 && this.parent) {
            canv.eventManager.openRecord();
            canv.canvasController.deleteProcessor(this.parent);
            canv.eventManager.closeRecord();
            canv.toolBox.TryGetToolByName("cursorTool").mode = "";
            selectionManager.clearSelected();
        } else if (shouldPersist) {
            canv.eventManager.openRecord();
            this.saveState("focus");
            canv.eventManager.closeRecord();
        }

        this.isEditing = false;
    }

    shouldStopInteractionOnModeChange(nextMode: string) {
        if (!this.isEditing) {
            return true;
        }

        if (nextMode === "resize" || nextMode === "sideResize") {
            return false;
        }

        return true;
    }

    getIsEditing() {
        return this.isEditing;
    }

    persistEditingDraft(newDelta: Delta) {
        if (this.editingAutosaveHandler === false) {
            return false;
        }

        this.syncEditingDraft(newDelta);

        if (this.editingAutosaveHandler) {
            this.editingAutosaveHandler();
            return true;
        }

        canv.eventManager.openRecord();
        this.saveState("focus");
        canv.eventManager.closeRecord();
        this.fixState("focus");

        return true;
    }

    syncEditingDraft(newDelta: Delta) {
        const sanitizedDelta = sanitizeDeltaLinkColor(newDelta);

        this.data.delta = sanitizedDelta;
        this.styleSummary = summarizeDelta(sanitizedDelta);
        this.rawOriginalLines = cloneLines(deltaToLines(sanitizedDelta));
    }

    setNewDelta(newDelta: Delta) {
        this.data.delta = sanitizeDeltaLinkColor(newDelta);

        this.styleSummary = summarizeDelta(newDelta);
        this.rawOriginalLines = cloneLines(deltaToLines(newDelta));

        const prevWidth = this.width;

        if (this.resizeMode === "sticker" && this.symbolsCount > 0) {
            this.fitSticker(true);
        } else {
            this.reflowFromRawLines();
            this.width = Math.max(prevWidth, this.width);
        }

        this.updateBase(this.startX, this.startY - this.lineHeight, this.width, this.height);
    }

    setStyleSummary(newSummary: SelectionSummary) {
        const { firstColor, secondColor } = normalizeColors(newSummary.color);

        this.styleSummary.firstColor = firstColor;
        this.styleSummary.secondColor = secondColor;
        this.styleSummary.isBold = newSummary.bold || false;
        this.styleSummary.isItalic = newSummary.italic || false;
        this.styleSummary.isStriked = newSummary.strike || false;
        this.styleSummary.isUnderline = newSummary.underline || false;
        this.styleSummary.link = newSummary.link || "";
    }

    getText(): string {
        return this.rawOriginalLines
            .map((line) => line.segments.map((segment) => segment.text).join(""))
            .join("\n");
    }

    getTextByLines(): string {
        return this.getText();
    }

    highlightText(text: string): void {
        const search = text.toLowerCase();
        if (!search) return;

        const fullText = this.getText().toLowerCase();
        const globalStart = fullText.indexOf(search);
        if (globalStart === -1) return;
        const globalEnd = globalStart + search.length - 1;

        let currentLineY = this.startY;
        let offset = 0;

        for (let index = 0; index < this.lines.length; index++) {
            const line = this.lines[index];
            const lineText = line.segments.map((segment) => segment.text).join("");
            const lineLength = lineText.length;

            if (lineLength === 0) {
                offset += 1;
                currentLineY += this.lineHeight;
                continue;
            }

            const lineStart = offset;
            const lineEnd = offset + lineLength - 1;

            if (globalStart <= lineEnd && globalEnd >= lineStart) {
                const localStart = Math.max(0, globalStart - lineStart);
                const localEnd = Math.min(lineLength - 1, globalEnd - lineStart);

                let prefixWidth = 0;
                let matchWidth = 0;
                let charOffset = 0;

                for (const segment of line.segments) {
                    const segmentText = segment.text;
                    const segmentLength = segmentText.length;
                    const segmentStart = charOffset;
                    const segmentEnd = charOffset + segmentLength - 1;
                    const font = getSegmentFont(segment, this.data);

                    if (localEnd < segmentStart) {
                        break;
                    }

                    if (localStart > segmentEnd) {
                        prefixWidth += measureTextWidth(segmentText, font);
                        charOffset += segmentLength;
                        continue;
                    }

                    if (localStart > segmentStart) {
                        const before = segmentText.substring(0, localStart - segmentStart);
                        prefixWidth += measureTextWidth(before, font);
                    }

                    const matchStart = Math.max(localStart, segmentStart) - segmentStart;
                    const matchEnd = Math.min(localEnd, segmentEnd) - segmentStart + 1;
                    const matchText = segmentText.substring(matchStart, matchEnd);
                    matchWidth += measureTextWidth(matchText, font);

                    charOffset += segmentLength;
                }

                if (matchWidth > 0) {
                    const lineWidth =
                        this.lineWidths[index] ??
                        calculateLineWidth(line, this.data, this.horizontalPadding);
                    const lineStartX = getLineStartX(
                        lineWidth,
                        this.drawStartX,
                        this.data.textAlign,
                    );
                    const textStartX = getTextStartX(
                        line,
                        lineStartX,
                        this.data,
                        this.horizontalPadding,
                    );
                    const rectY = currentLineY - this.lineHeight + this.verticalPadding;
                    const rectHeight = this.lineHeight - 2 * this.verticalPadding;

                    canv.ctx.save();
                    canv.ctx.globalAlpha = 0.3;
                    canv.ctx.fillStyle = "#FFA629";
                    canv.ctx.fillRect(textStartX + prefixWidth, rectY, matchWidth, rectHeight);
                    canv.ctx.restore();
                }
            }

            offset += lineLength + 1;
            currentLineY += this.lineHeight;
        }
    }

    getData(): TextV2Data {
        return {
            fontFamily: this.data.fontFamily,
            fontSize: this.data.fontSize,
            textAlign: this.data.textAlign,
            textVerticalAlign: this.data.textVerticalAlign,
            delta: this.data.delta,
        };
    }

    setData(data: TextV2Data): void {
        this.data.fontFamily = data.fontFamily;
        this.setFontSizeValue(data.fontSize);
        this.data.textAlign = data.textAlign;
        this.data.textVerticalAlign = data.textVerticalAlign;
        this.setNewDelta(data.delta);
    }

    getCurrentColor(): string {
        return this.styleSummary.firstColor;
    }

    searchText(text: string): Array<{
        status: boolean;
        textWidget: TextWidgetV2;
        mode: string;
    }> {
        const currentText = this.getText().toLowerCase();
        if (currentText.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this,
                    mode: "widget",
                },
            ];
        }
        return [];
    }

    setText(text: string): void {
        const normalizedText = this.onlyLineMode ? text.replace(/\n+/g, " ") : text;
        if (normalizedText.length === 0) {
            this.data.delta = new Delta();
        } else {
            const op: { insert: string; attributes?: { color: string } } = {
                insert: normalizedText,
            };

            if (this.styleSummary.firstColor) {
                op.attributes = { color: this.styleSummary.firstColor };
            }

            this.data.delta = new Delta([op]);
        }

        this.styleSummary = summarizeDelta(this.data.delta);
        this.rawOriginalLines = cloneLines(deltaToLines(this.data.delta));
        this.reflowFromRawLines();
    }

    // использовать метод только для кейса связанного с ИИ асистентом
    streamText(text: string): void {
        if (!text) return;

        const ops = this.data.delta.ops;

        if (!ops || ops.length === 0) {
            this.data.delta.ops = [{ insert: text }];
        } else {
            const lastOp = ops[ops.length - 1];
            if (typeof lastOp.insert === "string") {
                lastOp.insert += text;
            } else {
                ops.push({ insert: text });
            }
        }
        this.styleSummary = summarizeDelta(this.data.delta);
        this.rawOriginalLines = cloneLines(deltaToLines(this.data.delta));
        this.reflowFromRawLines();
    }

    clearText(): void {
        this.setText("");
    }

    getEmptyInputWidth(): number {
        const currentMetrics = calculateLineHeight(this.data);
        const defaultMetrics = calculateLineHeight({
            ...this.data,
            fontSize: DEFAULT_FONT_SIZE,
        });

        return Math.max(
            MIN_TEXT_WIDTH,
            currentMetrics.symbolWidth * 10,
            defaultMetrics.symbolWidth * 10,
        );
    }

    isOnlyEmptyLine(): boolean {
        if (this.rawOriginalLines.length !== 1) return false;
        return this.rawOriginalLines[0].segments.length === 0;
    }

    setGreyText(text: string): void {}
    menuMobileEditSet(textData = "") {}
    setKeepMinWidth(value: boolean) {}
    setAffectedItemsUniqValues() {}
    setEditTextMobile(widgetOptions: widgetOption[], UID: string) {}
    setCursorToEnd() {}
    menuLink() {}
    focusText(focusAtPointer = false) {
        canv.textEditingController.startEdit(this, focusAtPointer);
        this.isEditing = true;
    }
    unfocusText() {}
    focusTextByEnter() {}
    processDelete() {}

    getFixType(reason: string) {
        if (this.needFullState) {
            // getState может вызываться несколько раз в рамках одного события,
            // поэтому не сбрасываем флаг здесь.
            return "full";
        }

        switch (reason) {
            case "move":
                return "move";

            default:
                return "full";
        }
    }

    // Снимок данных для истории undo/redo:
    // нельзя сохранять ссылку на this.data, иначе before/after состояния
    // мутируют вместе и откат перестает работать предсказуемо.
    private getStateDataSnapshot(): TextV2Data {
        return {
            fontFamily: this.data.fontFamily,
            fontSize: this.data.fontSize,
            textAlign: this.data.textAlign,
            textVerticalAlign: this.data.textVerticalAlign,
            delta: new Delta(this.data.delta),
        };
    }

    getState<FixType extends keyof WidgetStateMap>(fixType: FixType): WidgetStateMap[FixType] {
        const state: WidgetStateMap = {
            move: {
                startX: this.startX,
                startY: this.startY,
            },
            full: {
                UID: this.UID,
                parentUID: this.parentUID,
                entityType: "textWidgetV2",
                startX: this.startX,
                startY: this.startY,
                resizeMode: this.resizeMode,
                hardWidth: this.hardWidth,
                aiGenerated: this.aiGenerated,
                // Всегда обнуляем legacy-поля Miro: они нужны только для единоразовой
                // миграции координат в loadState и не должны повторно применяться.
                miroX: 0,
                miroY: 0,
                miroWidth: 0,
                data: this.getStateDataSnapshot(),
            },
        };

        return state[fixType];
    }

    loadState<FixType extends keyof WidgetStateMap>(
        state: WidgetStateMap[FixType],
        fixType: FixType,
    ) {
        switch (fixType) {
            case "move": {
                const currentState = state as WidgetStateMap["move"];

                this.startX = getNumberProperty("startX", currentState);
                this.startY = getNumberProperty("startY", currentState);

                this.updateDims();

                break;
            }

            case "full": {
                const currentState = state as WidgetStateMap["full"];

                this.UID = getStringProperty("UID", currentState);
                this.parentUID = getStringProperty("parentUID", currentState);
                this.startX = getNumberProperty("startX", currentState);
                this.startY = getNumberProperty("startY", currentState);
                this.aiGenerated = getBoolProperty("aiGenerated", currentState);
                this.resizeMode = getStringProperty("resizeMode", currentState) as ResizeMode;
                this.data.fontFamily = getStringProperty("fontFamily", currentState.data);
                this.data.textAlign = getStringProperty(
                    "textAlign",
                    currentState.data,
                ) as TextAlign;
                this.data.textVerticalAlign =
                    (getStringProperty(
                        "textVerticalAlign",
                        currentState.data,
                    ) as TextVerticalAlign) || "center";
                this.setFontSizeValue(getNumberProperty("fontSize", currentState.data));
                this.data.delta = buildDeltaWithTextAlign(
                    currentState.data.delta,
                    this.data.textAlign,
                );

                const incomingHardWidth = getNumberProperty("hardWidth", currentState, 0);

                if (this.resizeMode === "auto") {
                    this.hardWidth = 0;
                } else if (incomingHardWidth > 0) {
                    this.hardWidth = incomingHardWidth;
                }

                this.styleSummary = summarizeDelta(this.data.delta);
                this.rawOriginalLines = cloneLines(deltaToLines(this.data.delta));
                const fontRequirements = getFontRequirementsForTextData(this.rawOriginalLines);
                this.restoreHardWidth();
                this.scheduleRefreshAfterFontLoad(this.data.fontFamily, fontRequirements);
                // Сразу считаем layout для текущих метрик (fallback/дефолтный шрифт),
                // а после загрузки нужного семейства scheduleRefreshAfterFontLoad сделает догоняющий refresh.
                this.refreshLayoutAfterFontLoad();

                const miroMigration = this.hasLegacyMiroCoords(currentState)
                    ? {
                          x: currentState.miroX,
                          y: currentState.miroY,
                          width: currentState.miroWidth,
                      }
                    : undefined;

                this.scheduleRefreshAfterFontLoad(
                    this.data.fontFamily,
                    fontRequirements,
                    miroMigration,
                );
            }
        }
    }

    setResizeMode(mode: ResizeMode) {
        this.resizeMode = mode;
    }

    get symbolsCount(): number {
        let count = 0;
        for (const line of this.rawOriginalLines) {
            for (const segment of line.segments) {
                count += segment.text.length;
            }
        }
        return count;
    }

    getLinkRunsByLine(lineIndex: number) {
        return this.linkRunsByLine[lineIndex] ?? [];
    }

    getLineIndexAtLocalY(localY: number) {
        const lineIndex = Math.floor(localY / this.lineHeight);

        if (lineIndex < 0 || lineIndex >= this.lines.length) {
            return -1;
        }

        return lineIndex;
    }

    refreshLayoutAfterFontLoad(): void {
        const metrics = calculateLineHeight(this.data);
        this.lineHeight = metrics.lineHeight;
        this.verticalPadding = metrics.verticalPadding;
        this.horizontalPadding = metrics.horizontalPadding;

        if (this.resizeMode === "sticker" && this.hardWidth > 0 && this.hardHeight > 0) {
            this.fitSticker(true);
        } else {
            this.reflowFromRawLines();
        }
    }

    /** private методы */

    private reflowFromRawLines(): void {
        this.ensureLinePresence();
        this.restoreHardWidth();

        if (this.resizeMode === "auto" && this.isOnlyEmptyLine()) {
            this.lines = [{ ...this.rawOriginalLines[0], segments: [] }];
            this.lineWidths = [this.getEmptyInputWidth()];
            this.applyLineHeights(this.lines);
            this.updateDims();
            this.rebuildLinkRuns();
            return;
        }

        //Добавлена проверка на resizeMode, при проблемах с отрисовкой/выходом текста за границы виджета и т.д. попробовать убрать
        const normalizedLines =
            this.resizeMode === "auto"
                ? wrapLinesByMaxSymbols(this.rawOriginalLines)
                : this.rawOriginalLines;
        const widthSource = getMaxLineWidth(
            calculateLineWidths(normalizedLines, this.data, this.horizontalPadding),
        );
        const targetWidth =
            (this.resizeMode === "shape" || this.resizeMode === "sticker") && this.hardWidth > 0
                ? this.hardWidth
                : widthSource;
        const reflow = reflowTextLines(
            normalizedLines,
            targetWidth,
            this.data,
            this.horizontalPadding,
            this.resizeMode !== "sticker" || this.fontSize <= STICKER_MIN_FONT_SIZE,
        );
        this.lines = reflow.lines;
        this.lineWidths = reflow.lineWidths;
        this.applyLineHeights(this.lines);
        this.updateDims();
        this.rebuildLinkRuns();
    }

    private applyLineHeights(lines: TextLine[]): void {
        for (const line of lines) {
            line.textHeight = this.lineHeight;
        }
    }

    private getLineWidthForDraw(line: TextLine, index: number): number {
        const cachedLineWidth = this.lineWidths[index];

        if (typeof cachedLineWidth === "number" && Number.isFinite(cachedLineWidth)) {
            return cachedLineWidth;
        }

        const lineWidth = calculateLineWidth(line, this.data, this.horizontalPadding);
        this.lineWidths[index] = lineWidth;

        return lineWidth;
    }

    private ensureLinePresence(): void {
        if (this.rawOriginalLines.length === 0) {
            this.rawOriginalLines = [{ segments: [] }];
        }
    }

    private updateDrawStartX(): void {
        if (this.data.textAlign === "center") {
            this.drawStartX = this.startX + this.width / 2;
        } else if (this.data.textAlign === "right") {
            this.drawStartX = this.startX + this.width;
        } else {
            this.drawStartX = this.startX;
        }
    }

    private rebuildLinkRuns() {
        this.linkRunsByLine = buildLinkRuns({
            uid: this.UID,
            lines: this.lines,
            lineWidths: this.lineWidths,
            lineHeight: this.lineHeight,
            horizontalPadding: this.horizontalPadding,
            drawStartX: this.drawStartX - this.startX,
            data: this.data,
        });
    }

    private scheduleRefreshAfterFontLoad(
        fontFamily: string,
        requirements: ReturnType<typeof getFontRequirementsForTextData>,
        miroMigration?: { x: number; y: number; width: number },
    ): void {
        if (miroMigration) {
            this.applyLegacyMiroCoordinates(miroMigration.x, miroMigration.y, miroMigration.width);
        }

        if (isFontRequirementsLoaded(fontFamily, requirements)) {
            return;
        }

        void ensureFontLoaded(fontFamily, requirements)
            .then(() => {
                if (this.data.fontFamily !== fontFamily) {
                    return;
                }

                this.refreshLayoutAfterFontLoad();

                if (miroMigration) {
                    this.applyLegacyMiroCoordinates(
                        miroMigration.x,
                        miroMigration.y,
                        miroMigration.width,
                    );
                }
            })
            .catch(() => {
                // Ошибка загрузки уже логируется в fontRegistry, здесь важно не допустить unhandled rejection.
            });
    }

    private hasLegacyMiroCoords(state: WidgetStateMap["full"]): state is WidgetStateMap["full"] & {
        miroX: number;
        miroY: number;
        miroWidth: number;
    } {
        return (
            typeof state.miroX === "number" &&
            typeof state.miroY === "number" &&
            typeof state.miroWidth === "number" &&
            state.miroWidth !== 0
        );
    }

    private applyLegacyMiroCoordinates(miroX: number, miroY: number, miroWidth: number) {
        // 1) берем фактическую ширину текста после переноса,
        // 2) позиционируем по центру Miro-координат,
        // 3) форсим сохранение full state (без miro* полей).
        const previousResizeMode = this.resizeMode;

        this.resizeMode = "shape";
        this.hardWidth = miroWidth;
        this.reflowFromRawLines();

        const textWidthForMiro = getMaxLineWidth(this.lineWidths);

        this.resizeMode = previousResizeMode;

        this.startX = miroX - textWidthForMiro / 2;
        this.startY = miroY - this.height / 2 + this.lineHeight;
        this.updateDims();
        this.needFullState = true;
    }

    private restoreHardWidth(): void {
        const isFixedMode = this.resizeMode === "shape" || this.resizeMode === "sticker";
        if (!isFixedMode || this.hardWidth > 0) {
            return;
        }

        this.calculateLineHeight();

        const widthSource = getMaxLineWidth(
            calculateLineWidths(this.rawOriginalLines, this.data, this.horizontalPadding),
        );

        this.hardWidth = Math.max(MIN_TEXT_WIDTH, widthSource);
        this.needFullState = true;
    }

    constructorName: "textV2" = "textV2";
    isFocused = false;
    isEditTextMobile = false;
    selection = {
        selectAll: (): void => {},
        removeSelection: (): void => {},
    };
    aiGenerated = false;
    fontBold = false;
    fontStrike = false;
    bullettedList = false;
    width = 0;
    height = 0;

    lines: TextLine[] = [];
    startX = 0;
    startY = 0;
    lineHeight = 0;
    fontSize = 0;
    hardWidth = 0;
    hardHeight = 0;
    onlyLineMode = false;
    resizeMode: ResizeMode = "auto";
    horizontalPadding = 0;
    private readonly data: TextV2Data;
    private rawOriginalLines: TextLine[] = [];
    private lineWidths: number[] = [];
    private verticalPadding = 0;
    private drawStartX = 0;
    private isEditing: boolean = false;
    private linkRunsByLine: TextLinkRunMetric[][] = [];
    styleSummary: StyleSummary;

    private resizeStartCatchPointer: string | null = null;
    private resizeDiagonalState: DiagonalResizeState | null = null;
    private resizeVerticalState: VerticalResizeState | null = null;
    private deferredResize: DeferredResize | null = null;
    private menuTextFontRequestId = 0;
    private needFullState = false;
    private resizeRafId: number | null = null;
    private pendingResizePayload: PendingResizePayload | null = null;
    private appliedResizeSignature: string | null = null;
    private isResizeSessionActive = false;
    private stickerResizeAnchor: {
        hardWidth: number;
        hardHeight: number;
        fontSize: number;
        lines: TextLine[];
    } | null = null;

    private editingAutosaveHandler: (() => void) | false | null = null;
    private skipNextHostInteractionSave = false;
    private skipNextInteractionSave = false;
}
